import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDynamicWaas } from '@dynamic-labs/sdk-react-core';
import bs58 from 'bs58';

import { toast } from '@sly-rentals/ui';

interface ImportKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Accepts either:
//   - Base58-encoded private key (the `solana-keygen pubkey -` output piped
//     through bs58)
//   - Raw 64-byte JSON array (Solana CLI default — `solana-keygen new`
//     writes ~/.config/solana/id.json in this shape).
//
// Returns the Base58 form expected by Dynamic's importPrivateKey.
function normalizeToBase58(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error('Empty private key.');
  }

  // JSON byte array — accept and convert.
  if (trimmed.startsWith('[')) {
    let bytes: number[];
    try {
      bytes = JSON.parse(trimmed);
    } catch {
      throw new Error('Could not parse byte array. Expected JSON like [1,2,3,…].');
    }
    if (!Array.isArray(bytes) || !bytes.every(n => Number.isInteger(n) && n >= 0 && n <= 255)) {
      throw new Error('Byte array must contain integers 0–255.');
    }
    if (bytes.length !== 64) {
      throw new Error(
        `Solana private keys are 64 bytes (got ${bytes.length}). Did you paste the public key by mistake?`
      );
    }
    return bs58.encode(Uint8Array.from(bytes));
  }

  // Otherwise assume Base58 — light validation.
  try {
    const decoded = bs58.decode(trimmed);
    if (decoded.length !== 64) {
      throw new Error(
        `Decoded key is ${decoded.length} bytes (expected 64). Make sure this is the secret key, not the public key.`
      );
    }
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('Decoded')) throw err;
    throw new Error('Input is not valid Base58 or a JSON byte array.');
  }
  return trimmed;
}

export function ImportKeyDialog({ open, onOpenChange }: ImportKeyDialogProps) {
  const { importPrivateKey } = useDynamicWaas();
  const [privateKey, setPrivateKey] = useState('');
  const [status, setStatus] = useState<
    { kind: 'idle' } | { kind: 'pending' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  if (!open) return null;

  const handleImport = async () => {
    setStatus({ kind: 'pending' });
    try {
      const base58 = normalizeToBase58(privateKey);
      await importPrivateKey({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        chainName: 'SOL' as any,
        privateKey: base58,
      });
      setPrivateKey('');
      setStatus({ kind: 'idle' });
      onOpenChange(false);
      toast.success('Private key imported', {
        description: 'The new wallet will appear in your Dynamic widget.',
      });
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  // Portal to <body> so the modal escapes any parent stacking / clipping
  // context (the playground sets `overflow: hidden` on a few containers, and
  // the page itself is inside a positioned shell that breaks `fixed inset-0`
  // when the dialog is rendered as a regular descendant).
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="import-key-overlay"
      onClick={() => onOpenChange(false)}
      role="dialog"
      aria-modal="true"
    >
      <div className="import-key-card" onClick={e => e.stopPropagation()}>
        <header className="import-key-head">
          <div className="kicker" style={{ marginBottom: 6 }}>
            [ wallet / import key ]
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 18,
              color: 'var(--ink)',
              margin: '0 0 6px',
              fontWeight: 500,
            }}
          >
            Import private key
          </h2>
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: 0, lineHeight: 1.55 }}>
            Paste either a Base58-encoded Solana private key or the raw JSON byte array from{' '}
            <span className="mono cyan">solana-keygen</span> (e.g.{' '}
            <span className="mono">[94,25,…,1]</span>). Dynamic will store it as an MPC-managed
            embedded wallet.
          </p>
        </header>

        <div className="import-key-body">
          <textarea
            value={privateKey}
            onChange={e => setPrivateKey(e.target.value)}
            placeholder="Base58 string OR [byte,byte,…]"
            className="import-key-textarea"
            autoComplete="off"
            spellCheck={false}
          />
          {status.kind === 'error' && <div className="import-key-err">{status.message}</div>}
        </div>

        <footer className="import-key-foot">
          <button type="button" className="sly-btn" onClick={() => onOpenChange(false)}>
            Close
          </button>
          <button
            type="button"
            className="sly-btn primary"
            onClick={handleImport}
            disabled={!privateKey.trim() || status.kind === 'pending'}
          >
            {status.kind === 'pending' ? 'Importing…' : 'Import'}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
